// Pure-function quote confidence scorer.
//
// Compares the in-progress quote against the contractor's training profile
// (their accepted-quote history) and returns a single intelligence object the
// UI can render directly. Runs client-side so it updates instantly as the
// contractor tweaks square feet, markup, etc.
//
// The scoring model:
//   - Pull the per-measurement-unit cost from the current quote (e.g. $/sqft).
//   - Compare to the contractor's accepted-quote avg for the same project type.
//   - Compute a signed delta %; bigger positive → contractor is pricing higher
//     than usual → win probability drops; negative → leaving money on table.

const WIN_PROB_BUCKETS = [
  // [min_delta_pct, max_delta_pct, win_prob_pct, level, message_template]
  [-Infinity, -25, 90, 'underpriced',
    "💰 You're pricing {abs}% under your average — likely to win, but worth checking if you're leaving money on the table."],
  [-25, -10,    85, 'underpriced',
    "💰 You're {abs}% under your average for similar jobs — strong win signal, but raise it a bit if you can."],
  [-10, 10,     80, 'sweet-spot',
    "✅ Right in your sweet spot — based on {n} similar closed deals, win probability ≈ 80%."],
  [10, 25,      55, 'caution',
    "⚠️ This quote is {delta}% above your average — win probability ≈ 55% based on past data."],
  [25, 50,      30, 'caution',
    "⚠️ This quote is {delta}% above your average — win probability drops to ≈ 30%. Consider the markup."],
  [50, Infinity, 15, 'high-risk',
    "🚩 This quote is {delta}% above your average for similar jobs — win probability ≈ 15%."],
];

const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const computeQuoteConfidence = ({ quote, training, projectTypeSlug, measurement }) => {
  if (!quote || !training || !training.trades || !projectTypeSlug) {
    return { state: 'unknown' };
  }

  const trade = training.trades[projectTypeSlug];
  if (!trade || !trade.sample_size) {
    return { state: 'unknown' };
  }

  const currentTotal = safeNumber(quote.total);
  const avgTotal = safeNumber(trade.avg_total);
  if (currentTotal <= 0 || avgTotal <= 0) {
    return { state: 'unknown' };
  }

  // Best signal: $/unit comparison if we have a positive measurement.
  // Falls back to raw total when measurement is missing (e.g. hourly with 0 hrs).
  const measure = safeNumber(measurement);

  let actualPerUnit = null;
  let avgPerUnit = null;
  if (measure > 0) {
    actualPerUnit = currentTotal / measure;
    // Avg per-unit derived from avg_material_rate + avg_labor_hours*labor_rate + markup
    const labRate = safeNumber(trade.avg_labor_rate) || 50;
    const baseUnitCost =
      safeNumber(trade.avg_material_rate) +
      safeNumber(trade.avg_labor_hours) * labRate;
    const markup = safeNumber(trade.avg_markup_percent) / 100;
    avgPerUnit = baseUnitCost * (1 + markup);
  }

  let deltaPct;
  if (actualPerUnit && avgPerUnit && avgPerUnit > 0) {
    deltaPct = ((actualPerUnit - avgPerUnit) / avgPerUnit) * 100;
  } else {
    deltaPct = ((currentTotal - avgTotal) / avgTotal) * 100;
  }

  const bucket = WIN_PROB_BUCKETS.find(([min, max]) => deltaPct >= min && deltaPct < max)
    || WIN_PROB_BUCKETS[2];
  const [, , winProb, level, template] = bucket;

  const message = template
    .replace('{delta}', String(Math.round(deltaPct)))
    .replace('{abs}', String(Math.round(Math.abs(deltaPct))))
    .replace('{n}', String(trade.sample_size));

  return {
    state: 'scored',
    level,
    win_probability: winProb,
    delta_percent: Math.round(deltaPct * 10) / 10,
    sample_size: trade.sample_size,
    avg_total: avgTotal,
    message,
  };
};
